import { useCenterStore } from '@caradasstore/CenterStore'
import { useLoadingStore } from '@caradasstore/LoadingStore'
import { useUserStore } from '@caradasstore/UserStore'
import { ItemPerPageSelector } from '@components/common'
import { NetworkParticipationModal } from '@components/network/network-participation-modal/NetworkParticipationModal'
import { AdasBox, AdasPagination, AdasPaper, AdasStack } from '@components/wrapper-components'
import { ChevronDown, ChevronUp, Check, Download, Info, Pencil, Search } from 'lucide-react'
import PropTypes from 'prop-types'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { getCalibrationCenter, getCalibrationCenterList } from '../../api/api'
import { NETWORK_TABLE_HEADERS } from '../../constants'
import { Logger } from '../../logger'
import { scrollPageTop } from '../../utils/useScrollTop'
import { ccidFormatter, downloadAsFile, roundToGreatestInteger } from '../../utils/utils'

/**
 * Calibration-center × network matrix, shared by the standalone /network page and
 * the Settings → Network Participation panel.
 *
 * `canEdit` gates every mutating affordance (pencil buttons, the edit modal, and
 * the row-click center switch). When omitted it defaults to the current user's
 * admin status (CenterStore `isCenterAdmin` OR system `is_admin` — the same signal
 * PageLayout/EditCalibrationForm use), so shop/store users get a read-only matrix
 * while admins can edit. The data source follows the same admin signal: admins get
 * the full center list; non-admins get the centers they can access (which is all a
 * shop can read, since the list endpoint is admin-only).
 *
 * `showHeading` renders the page-level title/description; turn it off when the host
 * (e.g. Settings) already provides its own header.
 */
export const NetworkParticipationMatrix = ({ canEdit: canEditProp, showHeading = true }) => {
  const [searchInput, setSearchInput] = useState('')
  const [calibrationCenters, setCalibrationCenters] = useState([])
  const [pageNo, setPageNo] = useState(0)
  const [totalCount, setTotalCount] = useState(0)
  const [pageSize, setPageSize] = useState(200)
  const [sort, setSort] = useState('ASC')
  const [sortField, setSortField] = useState('name')
  const [isActive, setIsActive] = useState(true)
  const [editCenter, setEditCenter] = useState(null)
  const setCenterDetail = useCenterStore((store) => store.setCenterDetail)
  const centerDetail = useCenterStore((store) => store.centerDetail)
  const accessibleCcIds = useCenterStore((store) => store.accessibleCcIds)
  const isCenterAdmin = useCenterStore((store) => store.isCenterAdmin)
  const isSystemAdmin = useUserStore((store) => !!store.currentUser?.is_admin)
  const setLoading = useLoadingStore((store) => store.setLoading)
  const history = useHistory()

  const isAdmin = isCenterAdmin || isSystemAdmin
  // Explicit prop wins (tests / forced read-only); otherwise fall back to admin status.
  const canEdit = canEditProp ?? isAdmin

  useEffect(() => {
    setLoading(true)
    // Same two-step "fetch totalCount, then fetch all" pattern as Manage Locations
    // so we never silently drop centers past the probe page size.
    const PROBE_PAGE_SIZE = 500

    // Admins can read the full network via the list endpoint.
    const fetchAllCenters = async () => {
      const initialResponse = await getCalibrationCenterList({ pageNo: 0, pageSize: PROBE_PAGE_SIZE })
      if (initialResponse?.status !== 200) return

      const total = initialResponse.data.totalCount || 0
      let allCenters = initialResponse.data.calibrationCenters || []

      if (total > allCenters.length) {
        const fullResponse = await getCalibrationCenterList({ pageNo: 0, pageSize: total })
        if (fullResponse?.status === 200) {
          allCenters = fullResponse.data.calibrationCenters || []
        }
      }

      setCalibrationCenters(allCenters)
      setTotalCount(total)
    }

    // GET /v1/calibrationcenters is admin-only (CAR-3670), so non-admins build the
    // matrix from the centers they can access — fetched per-ID like CalibrationCenterBar,
    // which is where the flag fields (is_sf_enabled …) live. Falls back to the active
    // center so the read-only view is never empty.
    const fetchAccessibleCenters = async () => {
      const ids = Array.from(
        new Set((accessibleCcIds && accessibleCcIds.length > 0 ? accessibleCcIds : [centerDetail?.id]).filter((id) => id != null))
      )
      if (ids.length === 0) {
        setCalibrationCenters([])
        setTotalCount(0)
        return
      }
      const results = await Promise.all(
        ids.map((ccid) =>
          getCalibrationCenter({ ccid })
            .then((res) => (res?.status === 200 ? res.data : null))
            .catch(() => null)
        )
      )
      const list = results.filter(Boolean)
      setCalibrationCenters(list)
      setTotalCount(list.length)
    }

    const fetchData = async () => {
      try {
        await (isAdmin ? fetchAllCenters() : fetchAccessibleCenters())
        scrollPageTop()
      } catch (error) {
        Logger.error({ message: error, payload: { file: 'NetworkParticipationMatrix', method: 'fetchData' } })
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [setLoading, isAdmin, accessibleCcIds, centerDetail?.id])

  const clickSortHandler = useCallback(
    (field) => {
      if (sortField === field) {
        setSort(sort === 'ASC' ? 'DESC' : 'ASC')
      } else {
        setSort('ASC')
        setSortField(field)
      }
    },
    [sort, sortField]
  )

  // Flag columns are limited to the networks the backend still returns — a flag
  // key omitted from every center means the referral source is globally disabled
  // (referral_source.is_enabled = false) and its column must not render.
  const tableHeaders = useMemo(() => {
    if (calibrationCenters.length === 0) return NETWORK_TABLE_HEADERS
    return NETWORK_TABLE_HEADERS.filter((col) => col.kind !== 'flag' || calibrationCenters.some((cc) => col.id in cc))
  }, [calibrationCenters])

  const filteredCenters = useMemo(() => {
    let list = calibrationCenters

    if (isActive !== '') {
      list = list.filter((cc) => cc.is_active === true)
    }

    if (searchInput) {
      const search = searchInput.toLowerCase()
      list = list.filter(
        (cc) =>
          cc.name?.toLowerCase().includes(search) ||
          `cc-${String(cc.id).padStart(4, '0')}`.includes(search) ||
          String(cc.id).includes(search)
      )
    }

    list = [...list].sort((a, b) => {
      const direction = sort === 'ASC' ? 1 : -1
      if (sortField === 'name') {
        return (a.name || '').localeCompare(b.name || '', 'en', { sensitivity: 'base' }) * direction
      }
      if (sortField === 'id') {
        return ((a.id ?? 0) - (b.id ?? 0)) * direction
      }
      return 0
    })

    return list
  }, [calibrationCenters, isActive, searchInput, sortField, sort])

  const pageCount = roundToGreatestInteger(filteredCenters.length / pageSize)
  const pagedCenters = filteredCenters.slice(pageNo * pageSize, pageNo * pageSize + pageSize)

  // Reset to page 1 whenever filters change so the user doesn't get stranded on an empty page.
  useEffect(() => {
    setPageNo(0)
  }, [searchInput, isActive])

  const paginationHandler = useCallback((_event, page) => {
    setPageNo(page - 1)
    scrollPageTop()
  }, [])

  const pageSizeHandler = useCallback((event) => {
    setPageNo(0)
    setPageSize(event.target.value)
    scrollPageTop()
  }, [])

  const handleCCSwitch = async (data) => {
    // Row-click switches the managed center — an admin-only affordance.
    if (!canEdit) return
    try {
      const ccResponse = await getCalibrationCenter({ ccid: data.id })
      if (ccResponse?.status === 200) {
        setCenterDetail(ccResponse.data)
        history.push('/')
      }
    } catch (error) {
      Logger.error({ message: error, payload: { file: 'NetworkParticipationMatrix', method: 'handleCCSwitch' } })
    }
  }

  const openEditModal = useCallback((event, cc) => {
    event.stopPropagation()
    setEditCenter({ id: cc.id, name: cc.name })
  }, [])

  // Merge the updated flag map from the modal into the matching row so the table
  // reflects the change immediately without re-fetching the whole list.
  const handleParticipationSaved = useCallback(
    (updatedFlags) => {
      if (!editCenter) return
      setCalibrationCenters((prev) => prev.map((cc) => (cc.id === editCenter.id ? { ...cc, ...updatedFlags } : cc)))
    },
    [editCenter]
  )

  const downloadNetworkCSV = useCallback(() => {
    let list = isActive !== '' ? calibrationCenters.filter((cc) => cc.is_active) : calibrationCenters
    const flagFields = tableHeaders.filter((h) => h.kind === 'flag')
    const rows = list.map((cc) => [`"${cc.name || ''}"`, ccidFormatter(cc.id), ...flagFields.map((field) => (cc[field.id] ? 'Yes' : 'No'))])
    const header = ['CC Name', 'CC #', ...flagFields.map((h) => h.label)]
    const csv = [header, ...rows].map((row) => row.join(',') + '\n')
    const fileName = isActive ? 'active_network_participation.csv' : 'all_network_participation.csv'
    downloadAsFile(new Blob(csv, { type: 'text/plain' }), fileName)
  }, [calibrationCenters, isActive, tableHeaders])

  const statusTabs = [
    { value: '', label: 'All' },
    { value: true, label: 'Active' }
  ]

  // Brand flags (excluding name/id) used for mobile card badges.
  const flagColumns = tableHeaders.filter((h) => h.kind === 'flag')

  return (
    <div className={showHeading ? 'mx-auto max-w-[1400px] px-3 py-6 min-[435px]:px-0' : 'w-full'}>
      {/* Heading + description (desktop only) */}
      {showHeading && (
        <AdasBox className='hidden min-[435px]:block pt-6 md:pt-4 mb-2'>
          <h1 className='text-[32px] font-bold leading-tight tracking-tight text-foreground'>National Agreement Network</h1>
          <p className='mt-1 text-[14px] text-[#6B7280]'>
            Control which calibration centers participate in the scheduler and each referral partner network.
          </p>
        </AdasBox>
      )}

      {/* Mobile persistent search */}
      <div className='min-[435px]:hidden mt-3 mb-2 flex items-center gap-2 rounded-lg border border-[#D1D5D8] bg-white px-3 py-2.5'>
        <Search className='h-4 w-4 text-[#9CA3AF]' />
        <input
          type='text'
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder='Search calibration centers...'
          className='flex-1 border-0 bg-transparent text-[14px] text-[#0A1628] placeholder:text-[#9CA3AF] focus:outline-none'
        />
      </div>

      {/* All / Active tabs */}
      <div className='mt-4 flex items-center gap-6 border-b border-[#EBEDF0]'>
        {statusTabs.map((t) => {
          const active = isActive === t.value
          return (
            <button
              key={String(t.value)}
              type='button'
              onClick={() => setIsActive(t.value)}
              className={`pb-3 text-[14px] font-medium transition-colors ${
                active ? 'text-[#1F6FEB] border-b-2 border-[#1F6FEB]' : 'text-[#6B7280] hover:text-[#334155]'
              }`}
            >
              {t.label}
            </button>
          )
        })}
      </div>

      {/* Desktop search + Export */}
      <AdasBox className='hidden min-[435px]:flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mt-5'>
        <div className='relative w-full max-w-[420px]'>
          <Search className='pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9CA3AF]' />
          <input
            type='text'
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder='Search calibration centers...'
            className='h-10 w-full rounded-lg border border-[#D1D5D8] bg-white pl-9 pr-3 text-[13px] text-[#0A1628] placeholder-[#9CA3AF] focus:border-[#1F6FEB] focus:outline-none'
          />
        </div>
        <button
          onClick={downloadNetworkCSV}
          className='inline-flex items-center gap-2 h-10 px-4 bg-white border border-[#D1D5D8] rounded-lg text-[13px] font-semibold text-[#0A1628] hover:bg-[#F8FAFC] transition-colors'
        >
          <Download className='h-4 w-4' />
          Export CSV
        </button>
      </AdasBox>

      {/* Hint */}
      {canEdit && (
        <div className='mt-3 flex items-center gap-1.5 text-[12px] text-[#6B7280]'>
          <Info className='h-3.5 w-3.5' />
          <span>Click a calibration center to edit its network participation.</span>
        </div>
      )}

      {/* Mobile card list */}
      <AdasBox className='min-[435px]:hidden mt-3 space-y-3'>
        {pagedCenters.length === 0 ? (
          <div className='rounded-xl border border-[#D1D5D8] bg-white px-4 py-10 text-center text-[14px] text-[#6B7280]'>
            No calibration centers found.
          </div>
        ) : (
          pagedCenters.map((cc) => {
            const enabled = flagColumns.filter((f) => cc[f.id])
            const interactiveProps = canEdit
              ? {
                  role: 'button',
                  tabIndex: 0,
                  onClick: () => handleCCSwitch(cc),
                  onKeyDown: (event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      handleCCSwitch(cc)
                    }
                  }
                }
              : {}
            return (
              <div
                key={cc.id}
                {...interactiveProps}
                className={`w-full rounded-xl border border-[#D1D5D8] bg-white p-4 text-left transition-colors ${
                  canEdit ? 'cursor-pointer hover:bg-[#F8FAFC]' : ''
                }`}
              >
                <div className='flex items-start justify-between gap-3'>
                  <div className='min-w-0 flex-1'>
                    <div className={`text-[15px] font-semibold leading-tight ${canEdit ? 'text-[#1F6FEB]' : 'text-[#0A1628]'}`}>
                      {cc.name}
                    </div>
                    <div className='mt-0.5 font-mono text-[12px] text-[#475569]'>{ccidFormatter(cc.id)}</div>
                  </div>
                  <div className='flex shrink-0 items-center gap-1.5'>
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${
                        cc.is_active ? 'bg-[#DCFCE7] text-[#16803F]' : 'bg-[#EBEDF0] text-[#6B7280]'
                      }`}
                    >
                      {cc.is_active ? 'Active' : 'Inactive'}
                    </span>
                    {canEdit && (
                      <button
                        type='button'
                        onClick={(event) => openEditModal(event, cc)}
                        aria-label={`Edit network participation for ${cc.name}`}
                        className='inline-flex h-8 w-8 items-center justify-center rounded-full text-[#475569] transition-colors hover:bg-[#EBEDF0] hover:text-[#1F6FEB]'
                      >
                        <Pencil className='h-4 w-4' />
                      </button>
                    )}
                  </div>
                </div>
                <div className='mt-3 border-t border-[#EBEDF0] pt-3'>
                  <div className='text-[10px] font-semibold uppercase tracking-wide text-[#6B7280]'>Participating in</div>
                  {enabled.length === 0 ? (
                    <div className='mt-1 text-[13px] text-[#6B7280]'>None</div>
                  ) : (
                    <div className='mt-1 flex flex-wrap gap-1.5'>
                      {enabled.map((flag) => (
                        <span
                          key={flag.id}
                          className='inline-flex items-center gap-1 rounded-full bg-[#DCFCE7] px-2 py-0.5 text-[11px] font-semibold text-[#16803F]'
                        >
                          <Check className='h-3 w-3' strokeWidth={3} />
                          {flag.label}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )
          })
        )}
      </AdasBox>

      {/* Desktop table */}
      <div className='hidden min-[435px]:block mt-4 overflow-hidden rounded-card border border-border bg-card shadow-level-1'>
        <div className='overflow-x-auto'>
          <table className='w-full text-left text-[13px]'>
            <thead>
              <tr className='bg-gradient-to-r from-[#0A1628] to-[#1F2D3D] text-white'>
                {tableHeaders.map((col) => {
                  const isSortable = col.sortable
                  const isSorted = sortField === col.id
                  return (
                    <th
                      key={col.id}
                      onClick={isSortable ? () => clickSortHandler(col.id) : undefined}
                      className={`px-4 py-3 text-[11px] font-bold uppercase tracking-wide ${
                        col.kind === 'flag' ? 'text-center' : 'text-left'
                      } ${isSortable ? 'cursor-pointer select-none' : ''}`}
                    >
                      <span className='inline-flex items-center gap-1'>
                        {col.label}
                        {isSortable &&
                          isSorted &&
                          (sort === 'ASC' ? <ChevronUp className='h-3.5 w-3.5' /> : <ChevronDown className='h-3.5 w-3.5' />)}
                      </span>
                    </th>
                  )
                })}
                {/* Edit (pencil) column at the end of the row — admins only. */}
                {canEdit && (
                  <th className='px-4 py-3 text-center text-[11px] font-bold uppercase tracking-wide'>
                    <span className='sr-only'>Edit</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {pagedCenters.length === 0 ? (
                <tr>
                  <td colSpan={tableHeaders.length + (canEdit ? 1 : 0)} className='px-4 py-10 text-center text-[#6B7280]'>
                    No calibration centers found.
                  </td>
                </tr>
              ) : (
                pagedCenters.map((cc) => (
                  <tr
                    key={cc.id}
                    onClick={canEdit ? () => handleCCSwitch(cc) : undefined}
                    className={`border-t border-[#EBEDF0] transition-colors even:bg-[#F8FAFC] ${
                      canEdit ? 'cursor-pointer hover:bg-[#F1F4F8]' : ''
                    }`}
                  >
                    {tableHeaders.map((col) => {
                      if (col.id === 'name') {
                        return (
                          <td
                            key={col.id}
                            className={`px-4 py-3 text-[13px] font-medium ${canEdit ? 'text-[#1F6FEB] hover:underline' : 'text-[#0A1628]'}`}
                          >
                            <span className='block max-w-[220px] truncate'>{cc.name}</span>
                          </td>
                        )
                      }
                      if (col.id === 'id') {
                        return (
                          <td key={col.id} className='px-4 py-3 font-mono text-[12px] text-[#475569]'>
                            {ccidFormatter(cc.id)}
                          </td>
                        )
                      }
                      // Flag cells: green check when enabled, gray em-dash when not.
                      return (
                        <td key={col.id} className='px-4 py-3 text-center'>
                          {cc[col.id] ? (
                            <span className='inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#DCFCE7] text-[#16803F]'>
                              <Check className='h-3.5 w-3.5' strokeWidth={3} />
                            </span>
                          ) : (
                            <span className='text-[#CBD5E1]'>—</span>
                          )}
                        </td>
                      )
                    })}
                    {/* Edit (pencil) cell at the end of the row — admins only. */}
                    {canEdit && (
                      <td className='px-4 py-3 text-center'>
                        <button
                          type='button'
                          onClick={(event) => openEditModal(event, cc)}
                          aria-label={`Edit network participation for ${cc.name}`}
                          className='inline-flex h-7 w-7 items-center justify-center rounded-full text-[#475569] transition-colors hover:bg-[#EBEDF0] hover:text-[#1F6FEB]'
                        >
                          <Pencil className='h-3.5 w-3.5' />
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <AdasPaper elevation={0} className='hidden min-[435px]:flex items-center justify-between p-[20px] mt-[20px]'>
        <AdasBox className='w-[20%]'>
          <ItemPerPageSelector pageSizeHandler={pageSizeHandler} pageSize={pageSize} />
        </AdasBox>
        <AdasBox>
          <AdasStack spacing={2}>
            <AdasPagination page={pageNo + 1} onChange={paginationHandler} count={pageCount} shape='rounded' />
          </AdasStack>
        </AdasBox>
      </AdasPaper>

      {/* Total count surfaced for parity with Manage Locations — kept in DOM as a no-op span so totalCount stays a tracked dependency. */}
      <span data-testid='network-total-count' hidden>
        {totalCount}
      </span>

      {canEdit && (
        <NetworkParticipationModal
          open={!!editCenter}
          center={editCenter}
          onClose={() => setEditCenter(null)}
          onSaved={handleParticipationSaved}
        />
      )}
    </div>
  )
}

NetworkParticipationMatrix.propTypes = {
  // When omitted, defaults to the current user's admin status.
  canEdit: PropTypes.bool,
  showHeading: PropTypes.bool
}
